import { useEffect, useRef, useState } from 'react';

/* ============================================================================
   GATE QR SCANNER

   The parent's pass already renders a QR carrying `passId:code`. What was
   missing was the other half: a guard could only TYPE both values, which at a
   3pm gate with forty parents queueing is the slowest possible way to do it,
   and the most error-prone — a mistyped 6-digit code reads as a rejected pass,
   which is exactly the alarm you do not want raised by a typo.

   TWO DECODE ENGINES, one camera path:

     1. window.BarcodeDetector — native, free, no download. Chrome + Android
        WebView, which is what most gate devices run.
     2. jsQR — lazily imported ONLY when the native detector is missing.

   ⚠ WHY THE FALLBACK EXISTS. Safari/iOS has never shipped BarcodeDetector and
   shows no sign of doing so. The old capability check required it, so on an
   iPhone the camera button never appeared at all and the guard was silently
   dropped to typing forever. If the gate device is an iPad or an iPhone — and
   at an Indian school it very often is — the scanner simply did not exist.

   Support now keys on getUserMedia alone, which is universal. The decoder is
   chosen after the camera opens.

   ⚠ jsQR is imported with a DYNAMIC import so Chrome never downloads it. It is
   ~50KB gzipped and would otherwise be dead weight in the main bundle for the
   majority of devices that have the native detector.

   ⚠ THE TYPED CODE REMAINS. This is an accelerator, never a replacement:
     • cameras fail, lenses get dirty, phones die
     • a parent may arrive with a screenshot on a cracked screen
   A gate that can ONLY scan is a gate that stops working on a bad day, so the
   manual fields stay exactly where they were and keep full parity.
   ============================================================================ */

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}
declare global {
  interface Window {
    BarcodeDetector?: {
      new(opts?: { formats?: string[] }): BarcodeDetectorLike;
      getSupportedFormats?: () => Promise<string[]>;
    };
  }
}

/**
 * Can this device scan at all?
 *
 * ⚠ Keys on getUserMedia ONLY — deliberately not on BarcodeDetector.
 *
 * The previous version required the native detector, which Safari/iOS does not have, so the whole
 * camera button was hidden on every iPhone and iPad. Any device with a camera can scan now; which
 * engine decodes the frames is decided later, inside the component.
 */
export function isScannerSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/** True when the browser decodes natively and jsQR is not needed. */
export function hasNativeDetector(): boolean {
  return typeof window !== 'undefined' && typeof window.BarcodeDetector !== 'undefined';
}

// ⚠ BOTH FORMS ACCEPTED — hyphenated and bare.
//
// The parent's QR encodes the pass id WITHOUT hyphens, because a v3-M byte-mode QR holds 42 bytes and
// `uuid:code` is 43. Stripping the four hyphens is what makes it fit.
//
// This regex previously demanded hyphens, so every scan of a real pass was rejected as "not one of
// ours" — the scanner and the thing it scans disagreed about the format. Accepting both means an
// older QR still works, which matters because a parent may have screenshotted one.
const GUID = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i;
const CODE = /^[0-9]{4,10}$/;

/**
 * Parse `passId:code` from a scanned QR — strictly.
 *
 * ⚠ Loose parsing is a trap here. Splitting on the last colon alone happily accepts
 * `https://something/qr`, because a URL contains a colon: the guard would then see a puzzling
 * "pass rejected" instead of "that isn't one of ours". At a gate, those two messages mean very
 * different things — one suggests a suspicious parent, the other a wrong QR.
 *
 * So both halves are validated for SHAPE before anything is submitted: a GUID pass id and a numeric
 * code. A supermarket loyalty card or a UPI QR is now rejected as what it is.
 */
export function parsePassPayload(raw: string): { passId: string; code: string } | null {
  const s = (raw ?? '').trim();
  const idx = s.lastIndexOf(':');
  if (idx <= 0) return null;
  const passId = s.slice(0, idx).trim();
  const code = s.slice(idx + 1).trim();
  if (!GUID.test(passId) || !CODE.test(code)) return null;

  // Re-insert the hyphens before submitting. The API binds a Guid, and while .NET parses the bare
  // 32-character form too, sending the canonical shape keeps the request readable in a log — which is
  // where anyone will look when a guard says "it didn't work".
  const canonical = passId.includes('-')
    ? passId
    : `${passId.slice(0, 8)}-${passId.slice(8, 12)}-${passId.slice(12, 16)}-${passId.slice(16, 20)}-${passId.slice(20)}`;

  return { passId: canonical, code };
}

export function QrScanner({ onScan, onClose }: {
  onScan: (passId: string, code: string) => void;
  onClose: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);   // jsQR path only
  const rafRef = useRef<number>(0);
  /**
   * De-duplication, keyed on the PASS, not on the payload.
   *
   * ⚠ THIS WAS THE DOUBLE-SCAN BUG. The guard held the camera on one pass and it submitted twice
   * within a second, producing an accepted scan immediately followed by "already used — do not
   * release the child" for a child who was standing right there.
   *
   * Three faults compounded:
   *
   *   1. The key was the whole payload `{passId}:{code}` — and the CODE ROTATES EVERY 30s. The
   *      parent's screen refreshed its code mid-scan, the string changed, and the same pass looked
   *      like a brand new one.
   *   2. The 2500ms timer cleared the guard unconditionally, even while the same pass was still
   *      filling the frame.
   *   3. onScan is fire-and-forget async, so the 60fps loop kept decoding DURING the network call
   *      and could submit again before the first response landed.
   *
   * Now: keyed on passId alone, never auto-cleared, and the loop is hard-stopped while a submission
   * is in flight. A pass is redeem-once — scanning it twice can only ever be wrong.
   */
  const scannedPassRef = useRef<string>('');    // passId already submitted this session
  const inFlightRef = useRef(false);            // a submission is running — stop decoding
  const [error, setError] = useState<string | null>(null);
  const [torchOn, setTorchOn] = useState(false);
  const [canTorch, setCanTorch] = useState(false);
  const [hint, setHint] = useState('Point the camera at the parent’s pass');

  /**
   * How the hint should READ, not just what it says.
   *
   * ⚠ A guard at a gate is holding a phone in one hand and a child's bag in the other. They are not
   * reading a sentence — they are glancing. Colour carries the message before the words do:
   * green = decoded, amber = trying and failing, red = wrong kind of code.
   */
  const [tone, setTone] = useState<'ok' | 'warn' | 'bad' | null>(null);

  /**
   * Torch. A school gate at 6pm in December is dark, and a phone screen held up to a camera in the
   * dark is the single most common reason a scan fails.
   *
   * ⚠ `torch` is not in the standard MediaTrackConstraintSet type, hence the casts. It is also
   * Android-only in practice — iOS Safari does not expose it, which is why the button is rendered
   * from a capability check rather than assumed.
   */
  const toggleTorch = async () => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      const next = !torchOn;
      await track.applyConstraints(
        { advanced: [{ torch: next }] } as unknown as MediaTrackConstraints);
      setTorchOn(next);
    } catch { /* torch refused — not worth an error, the guard can still scan */ }
  };

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      if (!isScannerSupported()) {
        setError('This device cannot scan. Type the code instead.');
        return;
      }
      try {
        // Rear camera on a phone; falls back to whatever exists on a laptop.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play();

        // Torch is a capability of the track, not of the browser — check the real one we opened.
        try {
          const caps = stream.getVideoTracks()[0]?.getCapabilities?.() as
            { torch?: boolean } | undefined;
          if (caps?.torch) setCanTorch(true);
        } catch { /* getCapabilities is absent on some browsers; no torch button, no harm */ }

        /**
         * Pick a decoder.
         *
         * ⚠ The jsQR import is INSIDE this branch on purpose. Vite splits it into its own chunk,
         * so a Chrome gate device never downloads it and an iPhone downloads it once.
         */
        let decode: (v: HTMLVideoElement) => Promise<string | null>;

        if (hasNativeDetector()) {
          const detector = new window.BarcodeDetector!({ formats: ['qr_code'] });
          decode = async (el) => {
            const found = await detector.detect(el);
            return found.length > 0 ? (found[0].rawValue?.trim() ?? '') : null;
          };
        } else {
          setHint('Starting the scanner…');
          const jsQR = (await import('jsqr')).default;
          if (cancelled) return;
          setHint('Point the camera at the parent’s pass');
          decode = async (el) => {
            const cv = canvasRef.current;
            if (!cv || !el.videoWidth) return null;
            // ⚠ Downscale to at most 640px on the long edge. Decoding a full 1080p frame every
            // animation frame pins the CPU on a cheap Android and the preview visibly stutters —
            // and a QR that fills a quarter of the frame decodes fine at this size.
            const scale = Math.min(1, 640 / Math.max(el.videoWidth, el.videoHeight));
            const w = Math.round(el.videoWidth * scale);
            const h = Math.round(el.videoHeight * scale);
            if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
            const ctx = cv.getContext('2d', { willReadFrequently: true });
            if (!ctx) return null;
            ctx.drawImage(el, 0, 0, w, h);
            const img = ctx.getImageData(0, 0, w, h);
            const r = jsQR(img.data, w, h, { inversionAttempts: 'dontInvert' });
            return r?.data?.trim() ?? null;
          };
        }

        // ⚠ HOW LONG WE HAVE BEEN POINTING AT NOTHING.
        //
        // Before this, a QR that could not be decoded produced NO branch at all: decode() returned
        // null and the loop simply span. The guard saw a live camera and the same neutral hint
        // forever, with no way to tell whether the code was unreadable, the camera was out of
        // focus, or the app was broken.
        //
        // That silence hid a genuinely malformed pass QR for days. A scanner that cannot read
        // something must SAY it cannot read it.
        const startedAt = Date.now();
        let sawAnything = false;

        const tick = async () => {
          if (cancelled || !videoRef.current) return;

          // ⚠ A submission is running (or has completed) — decode nothing further. Without this the
          // 60fps loop keeps reading the same pass DURING the network round trip and fires a second
          // submission before the first answer arrives.
          if (inFlightRef.current) return;

          try {
            const raw0 = await decode(videoRef.current);

            if (raw0 !== null && raw0 !== '') {
              sawAnything = true;
              const raw = raw0;
              const parsed = parsePassPayload(raw);
              if (parsed) {
                // ⚠ Keyed on the PASS ID, so a rotating code cannot make one pass look like two.
                // Never cleared: this scanner instance will not submit the same pass twice, full
                // stop. Closing and reopening the camera is the deliberate way to retry.
                if (parsed.passId !== scannedPassRef.current) {
                  scannedPassRef.current = parsed.passId;
                  inFlightRef.current = true;

                  setTone('ok');
                  setHint('Scanned — checking…');
                  onScan(parsed.passId, parsed.code);

                  // ⚠ STOP THE LOOP. Not a debounce — a stop. The result is on the screen behind
                  // this panel and the guard must look at it; continuing to decode would only ever
                  // re-submit something already answered.
                  return;
                }
              } else {
                // ⚠ A DECODED but WRONG code. Almost always a receipt QR, a WhatsApp code, or a
                // product barcode — say which problem it is, because "not working" and "wrong
                // code" need completely different responses from the guard.
                setTone('bad');
                setHint('That’s a QR code, but not a gate pass.');
              }
            } else if (!sawAnything && Date.now() - startedAt > 6000) {
              // ⚠ Six seconds of a live camera reading nothing. Tell the guard what to try instead
              // of leaving them holding a phone at a screen indefinitely.
              setTone('warn');
              setHint('Can’t read it yet — hold steadier, or type the 6-digit code below.');
            }
          } catch { /* a dropped frame is not an error worth showing */ }
          rafRef.current = requestAnimationFrame(() => { void tick(); });
        };
        void tick();
      } catch (e) {
        const name = (e as { name?: string })?.name;
        setError(name === 'NotAllowedError'
          ? 'Camera permission was refused. Allow it, or type the code instead.'
          : 'Could not open the camera. Type the code instead.');
      }
    };

    void start();
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [onScan]);

  return (
    <div className="card" style={{ padding: 14, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <strong style={{ fontSize: 14, flex: 1 }}>Scan the parent’s pass</strong>
        {canTorch && (
          <button className="btn" onClick={() => void toggleTorch()}
            aria-pressed={torchOn} title="Torch">
            {torchOn ? 'Light off' : 'Light on'}
          </button>
        )}
        <button className="btn" onClick={onClose}>Close camera</button>
      </div>

      {error ? (
        <div style={{ padding: 12, fontSize: 13, color: '#c1272d' }}>{error}</div>
      ) : (
        <>
          <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', background: '#000' }}>
            {/* ⚠ playsInline is load-bearing on iOS — without it Safari takes the video
                fullscreen and the guard loses the rest of the form. */}
            <video ref={videoRef} playsInline muted
              style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }} />
            {/* Offscreen scratch canvas for the jsQR path. Never shown. */}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {/* a simple aiming frame — helps a guard hold the phone at the right distance */}
            <div style={{
              position: 'absolute', inset: '18% 26%', border: '2px solid rgba(255,255,255,0.85)',
              borderRadius: 12, pointerEvents: 'none',
            }} />
          </div>
          <div style={{
            fontSize: 12.5, marginTop: 8, textAlign: 'center', fontWeight: tone ? 600 : 400,
            color: tone === 'ok' ? 'var(--ok, #0f7b48)'
                 : tone === 'bad' ? 'var(--danger, #b3261e)'
                 : tone === 'warn' ? 'var(--warn, #9a6b00)'
                 : 'var(--ink-2)',
          }}>{hint}</div>
        </>
      )}
    </div>
  );
}
